/**
 * CTA click tracking, in a file small enough to copy into the isolated workers.
 *
 * WHY THIS IS SPLIT OUT OF lib/analytics.ts
 * The blog and tools apps are separate Next builds that pin output tracing to
 * their own directories, so they cannot import from the parent app — anything
 * they need has to exist as a file inside them. `lib/analytics.ts` is 1,100
 * lines and already exists as a hand-maintained duplicate in apps/tools, which
 * is exactly how a fix can land in the root and never reach the live worker.
 * Copying it a third time would make that worse.
 *
 * So the ~30 lines a CTA actually needs live here, `lib/analytics.ts` re-exports
 * from this file rather than keeping its own copy, and
 * `scripts/sync-house-ads.mjs` generates the worker copies. One implementation,
 * three files, none of them written by hand.
 *
 * Events go through Zaraz in preference to gtag. That is not interchangeable:
 * the Google Tag Gateway's dataLayer here is configured with the Google Ads
 * AW-* id only, so an event sent via gtag never reaches GA4 at all. Zaraz's
 * server-side integration is what carries it. The blog's existing house-ad
 * tracking calls gtag directly and has presumably been missing from GA4 the
 * whole time; anything routed through here will not be.
 */

declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'set',
      action: string,
      params?: Record<string, unknown>
    ) => void;
    zaraz?: {
      track: (eventName: string, properties?: Record<string, unknown>) => void;
    };
  }
}

export type CtaLocation =
  | 'header'
  | 'hero'
  | 'inline'
  | 'footer'
  | 'tool_cta'
  | 'blog_cta'
  | 'kb_cta';

/** Send one GA4 event, preferring Zaraz. Never throws — a tracking failure must
 *  not take a click with it. */
export function trackEvent(action: string, params?: Record<string, unknown>): void {
  try {
    if (typeof window === 'undefined') return;
    if (window.zaraz && typeof window.zaraz.track === 'function') {
      window.zaraz.track(action, params);
    } else if (typeof window.gtag === 'function') {
      window.gtag('event', action, params);
    }
  } catch {
    // Analytics is never worth breaking a page for.
  }
}

/** Track a call-to-action click. */
export function trackCtaClick(
  ctaText: string,
  ctaLocation: CtaLocation,
  destinationUrl: string
): void {
  trackEvent('cta_click', {
    event_category: 'conversion',
    // Also sent under the GA4-registered custom dimension so clicks are
    // attributable in reporting, not just countable.
    cta_name: ctaText,
    cta_text: ctaText,
    cta_location: ctaLocation,
    destination_url: destinationUrl,
  });
}
